import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, useContext } from "react";
import videojs from "video.js";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import {isMobile} from "react-device-detect";

// Models
import VideoDTO from "../../../../../../Models/DTOs/VideoDTO";
import {IPlayerPeakInfo} from "../../../../../../Models/IPlayerPeakInfo";

// Components
import VideoProgressBar from "./VideoProgressBar";
import BigCenteredControls from "./BigCenteredControls";
import VideoFullscreen from "./VideoFullScreen";
import VideoSettings from "./VideoSettings";
import VideoTheatreMode from "./VideoTheatreMode";
import VideoPlayPause from "./VideoPlayPause";
import VideoTimeDisplay from "./VideoTimeDisplay";
import VideoVolume from "./VideoVolume";
import VideoSubtitles from "./VideoSubtitles";
import LoadingOverlay from "../../../../../LoadingOverlay";

// Context
import {GlobalInterfaceContext} from "../../../../../../Context/GlobalInterfaceContext";
import {UserAuthenticationContext} from "../../../../../../Context/UserAuthenticationContext";
import {VideoPlayerSettingsContext} from "../../../../../../Context/VideoPlayerSettingsContext";

// Hooks
import useTelemetry from "../../../../../../Hooks/Content/Watch/VideoSingle/useTelemetry";
import useVideoView from "../../../../../../Hooks/Reporting/Video/useVideoView";
import useVideoProgress from "../../../../../../Hooks/Content/Watch/VideoSingle/useVideoProgress";
import useSubtitleTracks from "../../../../../../Hooks/Content/Watch/VideoSingle/useSubtitleTracks";

// Helpers
import { CanAccessMemberOnlyContent } from "../../../../../../Helpers/Subscription";

interface VideoPlayerProps {
    video?: VideoDTO;
    src?: string;
    autoPlay: boolean;
    playbackAllowed: boolean;
    isTheatreMode?: boolean;
    setIsTheatreMode?: React.Dispatch<React.SetStateAction<boolean>>;
    peak?: IPlayerPeakInfo | null;
    updatePeak?: (currentPeak: number) => Promise<boolean>;
    poster?: string;
    isCommentInputFocused?: boolean;
    useTrailerLink?: boolean;
    isActive?: boolean;
    unloadOnInactive?: boolean;
}

// Wait time before hiding controls if no user input provided
const INACTIVITY_TIMEOUT = 3000; // ms

const VideoPlayer: React.FC<VideoPlayerProps> = (
    {
        video,
        src,
        autoPlay,
        playbackAllowed,
        isTheatreMode,
        setIsTheatreMode,
        peak,
        updatePeak,
        poster,
        isCommentInputFocused,
        useTrailerLink,
        isActive,
        unloadOnInactive
    }
) => {
    const

        /** =============================
         * Refs
         ============================= */
        containerRef = useRef<HTMLDivElement | null>(null),
        videoHostRef = useRef<HTMLDivElement | null>(null),
        fullScreenRef = useRef<HTMLDivElement | null>(null),
        playerRef = useRef<Player | null>(null),
        isCommentInputFocusedRef = useRef(isCommentInputFocused),

        wasPlayingRef = useRef(false),
        resumeTimeRef = useRef(0),
        lastSrcRef = useRef<string | undefined>(undefined),

        // tracks the timeout before hiding controls if user provides no further input
        inactivityTimerRef = useRef<number | null>(null),

        // mirrors openControlMenu so hideControls can read the latest value without
        // gaining a new identity each time a pop-up menu opens/closes (which would
        // otherwise re-subscribe the inactivity timer / keydown / playback listeners)
        openControlMenuRef = useRef<"" | "settings" | "subtitles">(""),

        /** =============================
         * Context
         ============================= */
        { hasUserInteracted } = useContext(GlobalInterfaceContext),
        { userData, getAccessTokenSync, getValidAccessToken, doesAuthTokenExist } = useContext(UserAuthenticationContext),
        { setMuted } = useContext(VideoPlayerSettingsContext),

        /** =============================
         * State
         ============================= */
        // True when video-single.js has fully initialized
        [videoPlayerReady, setVideoPlayerReady] = useState(false),
        [playerInstance, setPlayerInstance] = useState<Player | null>(null),

        // State that shows / hides player controls (Play/pause has its own, slightly modified rules)
        [showControls, setShowControls] = useState(true),

        // Tracks if video-single is playing or not
        [isPlaying, setIsPlaying] = useState(false),

        // Tracks if video-single uses fullscreen or not
        [isFullscreen, setIsFullscreen] = useState(false),

        // Which shelf popup is open — settings gear or subtitles ("" = none)
        [openControlMenu, setOpenControlMenu] = useState<"" | "settings" | "subtitles">(""),
        [isVolumeHoveredOver, setIsVolumeHoveredOver] = useState(false),

        // Temporary PopUp Trigger
        [isAlbionPopUpOpen, setIsAlbionPopUpOpen] = useState(false),

        /** =============================
         * Functions
         ============================= */
            // Hides player controls
        hideControls = useCallback(() => {
            // Keep the shelf (and any open settings / subtitles menu) on screen
            // while the user is mid-interaction with a pop-up menu. Read through a
            // ref so this callback stays stable across menu toggles.
            if (openControlMenuRef.current !== "") return;

            if (playerRef.current && !playerRef.current.paused()) {
                setShowControls(false);
                setTimeout(() => {
                    setOpenControlMenu("");
                }, 750);
            }
        }, []),

        togglePlay = useCallback(() => {
            const player = playerRef.current;
            if (!player) return;

            if (player.paused()) {
                player.play();
                setIsPlaying(true);
            } else {
                player.pause();
                setIsPlaying(false);
            }
        }, []),

        // Restarts the inactivity timer if user provides input over the player
        resetInactivityTimer = useCallback(() => {
            if (inactivityTimerRef.current) {
                window.clearTimeout(inactivityTimerRef.current);
            }

            inactivityTimerRef.current = window.setTimeout(() => {
                hideControls();
            }, INACTIVITY_TIMEOUT);
        }, [hideControls]),

        // Calls the timer reset function and sets the show/hide state
        showControlsAndResetTimer = useCallback(() => {
            setShowControls(true);
            resetInactivityTimer();
        }, [resetInactivityTimer]),

        toggleControlMenu = (menu: "settings" | "subtitles", newIsOpen: boolean) => {
            if (!showControls) return;

            setOpenControlMenu(newIsOpen ? menu : "");

            // Opening a menu must not toggle playback (the previous mobile
            // behaviour) — it should only surface the pop-up. Hold the controls
            // open while the menu is on screen, then resume the auto-hide
            // countdown once it closes.
            if (newIsOpen) {
                if (inactivityTimerRef.current) {
                    window.clearTimeout(inactivityTimerRef.current);
                    inactivityTimerRef.current = null;
                }
            } else {
                resetInactivityTimer();
            }
        },

        /** =============================
         * Input Handlers
         ============================= */
        handlePointerMove = () => {
            showControlsAndResetTimer();
        },

        handlePointerTogglePlay = (e: React.MouseEvent) => {
            if (e.button === 0) {
                togglePlay();
                showControlsAndResetTimer();
            }
        },

        handleOnTouchStart = () => {
            if (!showControls && isPlaying) {
                showControlsAndResetTimer();
                return;
            }

            if (showControls && isPlaying) {
                hideControls();
            }
        },

        skipBack10Seconds = useCallback(() => {
            const player = playerRef.current;
            if (player) {
                const currentTime = player.currentTime();

                if (currentTime) {
                    player.currentTime(Math.max(currentTime - 10, 0));
                    showControlsAndResetTimer();
                }
            }
        }, []),

        skipForward10Seconds = useCallback(() => {
            const player = playerRef.current;
            if (player) {
                const currentTime = player.currentTime();
                const duration = player.duration();

                if (currentTime && duration) {
                    player.currentTime(Math.min(currentTime + 10, duration));
                    showControlsAndResetTimer();
                }
            }
        }, []),

        toggleFullscreen = useCallback(() => {
            const el = fullScreenRef.current;
            if (!el) return;

            const doc: any = document;

            const isFull =
                doc.fullscreenElement === el ||
                doc.webkitFullscreenElement === el ||
                doc.msFullscreenElement === el;

            if (!isFull) {
                if (el.requestFullscreen) {
                    el.requestFullscreen();
                }
            } else {
                if (doc.exitFullscreen) {
                    doc.exitFullscreen();
                } else if (doc.webkitExitFullscreen) {
                    doc.webkitExitFullscreen();
                } else if (doc.msExitFullscreen) {
                    doc.msExitFullscreen();
                }
            }
        }, []),

        toggleMute = useCallback(() => {
            const player = playerRef.current;
            if (!player) return;

            const currentlyMuted = player.muted();
            player.muted(!currentlyMuted);
            setMuted(!currentlyMuted);

            showControlsAndResetTimer();
        }, [showControlsAndResetTimer]);

    /** =============================
     * Initialize the player
     ============================= */
    useLayoutEffect(() => {
        const videoHost = videoHostRef.current;
        if (!videoHost) return;
        const
            shouldAutoplay = hasUserInteracted && autoPlay,
            // Full videos require an explicitly authorized source. Metadata is never a fallback.
            source = src !== undefined ? src : (useTrailerLink ? video?.TrailerLink : undefined);

        lastSrcRef.current = source;

        if (!playerRef.current && source && playbackAllowed) {
            // Video.js owns/removes its element. Create a fresh node for each grant,
            // source change and StrictMode replay instead of reusing a detached ref.
            const videoElement = document.createElement("video");
            videoElement.className = "video-js ickonic-video-player";
            videoHost.appendChild(videoElement);

            const options = {
                autoplay: shouldAutoplay,
                controls: false,
                // CSS owns responsive sizing and the app uses custom controls,
                // so Video.js does not need a second element resize observer.
                resizeManager: false,
                sources: [{ src: source, type: "application/x-mpegURL" }]
            };

            playerRef.current = videojs(videoElement, options, () => {
                setVideoPlayerReady(true);

                if (shouldAutoplay) {
                    setIsPlaying(true);
                    showControlsAndResetTimer();
                }

                // set default focus to container for space-bar play-pause support
                if (containerRef && containerRef.current) {
                    containerRef.current.focus();
                }
            });
            setPlayerInstance(playerRef.current);
        }

        // Cleanup player and state on unmount
        return () => {
            if (playerRef.current) {
                playerRef.current.pause();
                playerRef.current.src([] as any);
                playerRef.current.load();
                playerRef.current.dispose();
                playerRef.current = null;
                setPlayerInstance(null);
                setVideoPlayerReady(false);
            }
            videoHost.replaceChildren();
        };
    }, [video?.Id, video?.TrailerLink, src, useTrailerLink, playbackAllowed]);

    // Use the actual player identity so recreating Video.js cannot leave save
    // listeners attached to a disposed instance while the ready flag stays true.
    useVideoProgress(
        videoPlayerReady ? playerInstance : null,
        video?.Id,
        peak,
        updatePeak,
        !useTrailerLink && !!userData?.id && peak?.userId === userData.id
    );

    /** =============================
     * If the video-single is paused, show controls
     ============================= */
    useEffect(() => {
        if (playerRef.current && playerRef.current.paused()) {
            showControlsAndResetTimer();
        }
    }, [playerRef, showControlsAndResetTimer]);

    /** =============================
     * Keep isPlaying in sync with the player itself — playback can also be
     * started/stopped outside our UI (media keys, PiP, browser context menu)
     ============================= */
    useEffect(() => {
        if (videoPlayerReady) {
            const player = playerRef.current;
            if (!player) return;

            const handlePlay = () => {
                setIsPlaying(true);
            };

            const handlePauseOrEnded = () => {
                setIsPlaying(false);
                showControlsAndResetTimer();
            };

            player.on("play", handlePlay);
            player.on("pause", handlePauseOrEnded);
            player.on("ended", handlePauseOrEnded);

            return () => {
                player.off("play", handlePlay);
                player.off("pause", handlePauseOrEnded);
                player.off("ended", handlePauseOrEnded);
            };
        }
    }, [videoPlayerReady, showControlsAndResetTimer, playerRef]);

    useEffect(() => {
        isCommentInputFocusedRef.current = isCommentInputFocused;
    }, [isCommentInputFocused]);

    // Keep the ref read by hideControls in sync with the live menu state
    useEffect(() => {
        openControlMenuRef.current = openControlMenu;
    }, [openControlMenu]);

    /** =============================
     * Global keydown handler for spacebar and back / fwd skip
     ============================= */
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            const activeElement = document.activeElement;
            const isInputFocused = activeElement instanceof HTMLElement && Boolean(
                activeElement.closest(
                    "input, textarea, select, button, a, summary, " +
                    "[contenteditable='true'], [role='button'], [role='link']"
                )
            );

            if (isInputFocused) {
                return;
            }

            if (isCommentInputFocusedRef.current) return;

            if (e.key === " " || e.code === "Space") {
                e.preventDefault();
                togglePlay();
                showControlsAndResetTimer();
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                skipBack10Seconds();
                showControlsAndResetTimer();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                skipForward10Seconds();
                showControlsAndResetTimer();
            } else if (e.key === "f" || e.key === "F") {
                e.preventDefault();
                toggleFullscreen();
                showControlsAndResetTimer();
            } else if (e.key === "m" || e.key === "M") {
                e.preventDefault();
                toggleMute();
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => {
            window.removeEventListener("keydown", handleGlobalKeyDown);
        };
    }, [togglePlay, showControlsAndResetTimer, skipBack10Seconds, skipForward10Seconds]);

    /** =============================
     * Listen for fullscreen exit
     ============================= */
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFull =
                document.fullscreenElement === fullScreenRef.current ||
                (document as any).webkitFullscreenElement === fullScreenRef.current ||
                (document as any).msFullscreenElement === fullScreenRef.current;

            setIsFullscreen(isFull);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
        document.addEventListener("msfullscreenchange", handleFullscreenChange);

        return () => {
            if (inactivityTimerRef.current) {
                window.clearTimeout(inactivityTimerRef.current);
            }
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
            document.removeEventListener("msfullscreenchange", handleFullscreenChange);
        };
    }, []);

    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;

        if (isActive === false) {
            wasPlayingRef.current = !player.paused();
            resumeTimeRef.current = player.currentTime() || 0;

            player.pause();
            setIsPlaying(false);

            if (unloadOnInactive) {
                player.src([] as any);
                player.load();
            }
        } else {
            if (unloadOnInactive && lastSrcRef.current) {
                player.src([{ src: lastSrcRef.current, type: "application/x-mpegURL" }]);
                player.load();
            }
            if (resumeTimeRef.current > 0) {
                player.currentTime(resumeTimeRef.current);
            }
            if (wasPlayingRef.current) {
                playerRef?.current?.play()?.catch(() => {});
                setIsPlaying(true);
            }
        }
    }, [isActive, unloadOnInactive]);

    const ALBION_POPUP_TRIGGER_SECONDS = 50 * 60 + 31; // 3031
    const albionPopupTriggeredRef = useRef(false);

    useEffect(() => {
        const player = playerRef.current;
        if (!videoPlayerReady || !player) return;

        const isAlbionVideo = video?.Id === 1624;
        albionPopupTriggeredRef.current = false;

        if (!isAlbionVideo) return;

        const isSubscribedUser = CanAccessMemberOnlyContent(
            userData?.subscription?.status,
            userData?.subscription?.plan_id,
            userData?.admin_role_id,
            userData?.subscription?.is_entitled
        );

        const onTimeUpdate = () => {
            if (isSubscribedUser) return;
            if (albionPopupTriggeredRef.current) return;
            if (isAlbionPopUpOpen) return;

            const t = player.currentTime() || 0;

            if (t >= ALBION_POPUP_TRIGGER_SECONDS) {
                albionPopupTriggeredRef.current = true;
                setIsAlbionPopUpOpen(true);
            }
        };

        player.on("timeupdate", onTimeUpdate);

        return () => {
            player.off("timeupdate", onTimeUpdate);
        };
    }, [videoPlayerReady, video?.Id, userData]);

    useTelemetry(playerRef.current, {
        accessToken: userData ? userData.id : "",
        video,
        peak
    });

    useVideoView(playerRef.current, video?.Id, { getValidAccessToken, getAccessTokenSync, doesAuthTokenExist });

    const {
        subtitleTracks,
        activeLanguage: activeSubtitleLanguage,
        isShowing: isSubtitlesShowing,
        selectSubtitles,
        toggleSubtitles
    } = useSubtitleTracks(videoPlayerReady ? playerRef.current : null);

    return (
        <div
            className={ `video-player ${isTheatreMode ? 'is-theatre-mode' : 'is-not-theatre-mode' }` }
            ref={containerRef}
            style={{ position: "relative" }}
            onPointerMove={() => handlePointerMove()}
            tabIndex={0}
        >
            <div
                ref={fullScreenRef}
                style={{ width: "100%" }}
                className={`video-player__inner u-relative_hidden ${isFullscreen ? 'is-full-screen' : 'is-not-full-screen'}`}
            >
                {poster && (
                    <div className="u-full_cover_absolute" style={{
                        background: `url(${poster}) center/cover no-repeat`,
                        zIndex: 1,
                        display: isPlaying || (playerRef.current?.currentTime() ?? 0) > 0 ? 'none' : 'block'
                    }} />
                )}

                {!isMobile ? (
                    <div
                        className={`video-player__cover-overlay u-full_cover_absolute ${showControls ? 'is-hidden' : 'is-visible'}`}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            handlePointerTogglePlay(e);
                        }}
                        role="button"
                    />
                ) : (
                    <div
                        className={`video-player__cover-overlay u-full_cover_absolute ${showControls ? 'is-hidden' : 'is-visible'}`}
                        onTouchStart={handleOnTouchStart}
                        role="button"
                    />
                )}

                <div ref={videoHostRef} data-vjs-player style={{ position: "absolute", inset: 0 }} />

                {playerRef.current && (
                    <>
                        <BigCenteredControls
                            isPlaying={isPlaying}
                            onMouseDown={handlePointerTogglePlay}
                            showVideoPlayerOtherControls={showControls}
                            hideControls={hideControls}
                            showControlsAndResetTimer={() => showControlsAndResetTimer()}
                            isMobile={isMobile}
                            skipBack={skipBack10Seconds}
                            skipForward={skipForward10Seconds}
                        />

                        {videoPlayerReady && (
                            <div
                                className={`video-player__video-controls ${showControls ? 'is-not-hidden' : 'is-hidden'}`}
                            >
                                <div className="video-controls__shelf">
                                    <div className="video-controls__left-side">
                                        <VideoPlayPause
                                            isPlaying={isPlaying}
                                            onTogglePlay={togglePlay}
                                            showControls={showControls}
                                        />

                                        <VideoVolume
                                            isHovered={isVolumeHoveredOver}
                                            setIsHovered={setIsVolumeHoveredOver}
                                            player={playerRef.current}
                                        />

                                        <VideoTimeDisplay
                                            player={playerRef.current}
                                            isMobile={isMobile}
                                            isVolumeHoveredOver={isVolumeHoveredOver}
                                        />
                                    </div>

                                    <VideoSubtitles
                                        subtitleTracks={subtitleTracks}
                                        activeLanguage={activeSubtitleLanguage}
                                        isShowing={isSubtitlesShowing}
                                        onToggle={toggleSubtitles}
                                        onSelect={selectSubtitles}
                                        isOpen={openControlMenu === "subtitles"}
                                        onToggleOpen={(newIsOpen) => toggleControlMenu("subtitles", newIsOpen)}
                                    />

                                    <VideoSettings
                                        player={playerRef.current}
                                        isOpen={openControlMenu === "settings"}
                                        onToggleOpen={(newIsOpen) => toggleControlMenu("settings", newIsOpen)}
                                        subtitleTracks={subtitleTracks}
                                        activeSubtitleLanguage={activeSubtitleLanguage}
                                        onSelectSubtitles={selectSubtitles}
                                    />

                                    { setIsTheatreMode !== undefined && isTheatreMode !== undefined && (
                                        <VideoTheatreMode
                                            isTheatreMode={isTheatreMode}
                                            setIsTheatreMode={setIsTheatreMode}
                                            showControls={showControls}
                                        />
                                    )}

                                    <VideoFullscreen
                                        fullScreenRef={fullScreenRef}
                                        isFullscreen={isFullscreen}
                                        setIsFullscreen={setIsFullscreen}
                                        showControls={showControls}
                                    />
                                </div>

                                <VideoProgressBar
                                    player={playerRef.current}
                                />
                            </div>
                        )}
                    </>
                )}

                <LoadingOverlay
                    hideLoader={videoPlayerReady}
                    size="is-sixteen-nine-aspect-ratio"
                />
            </div>
        </div>
    );
};

export default VideoPlayer;
